import { useEffect, useMemo, useState } from "react";
import { Card } from "../../../components/base/Card";
import { DataTable, type DataTableColumn } from "../../../components/feature/DataTable";
import { Badge } from "../../../components/base/Badge";
import { CurrencyDisplay } from "../../../components/currency/CurrencyDisplay";
import { formatDate } from "../../../utils/format";
import { GALLERY_COLLECTIONS, GALLERY_GROUP_OPTIONS, GALLERY_STATUS_OPTIONS, type GalleryCollectionRow } from "../../../mocks/gallery";
import { memberService, groupService } from "@/services";

export function DataTableSection() {
  const [selectedRows, setSelectedRows] = useState<GalleryCollectionRow[]>([]);
  const [rows, setRows] = useState<GalleryCollectionRow[]>(GALLERY_COLLECTIONS);
  const [groupOptions, setGroupOptions] = useState(GALLERY_GROUP_OPTIONS);

  useEffect(() => {
    let active = true;
    Promise.all([
      memberService.listMembers().catch(() => []),
      groupService.listGroups().catch(() => []),
    ]).then(([members, groups]) => {
      if (!active) return;
      if (groups && groups.length > 0) {
        setGroupOptions(groups.map((g) => ({ value: g.name, label: g.name })));
      }
      if (members && members.length > 0) {
        const dynamicRows: GalleryCollectionRow[] = [];
        members.forEach((m, idx) => {
          if (m.enrollments && m.enrollments.length > 0) {
            m.enrollments.forEach((e) => {
              dynamicRows.push({
                id: `${m.id}-${e.groupId}`,
                member: m.fullName,
                group: e.groupName,
                amount: e.monthlyInstallment || 10000,
                date: e.joinedOn ? e.joinedOn.split("T")[0] : new Date().toISOString().split("T")[0],
                status: e.status === "completed" ? "paid" : e.status === "blacklisted" ? "overdue" : "paid",
              });
            });
          } else {
            dynamicRows.push({
              id: m.id,
              member: m.fullName,
              group: "—",
              amount: 10000,
              date: m.createdAt ? m.createdAt.split("T")[0] : new Date().toISOString().split("T")[0],
              status: m.status === "active" ? "paid" : "pending",
            });
          }
        });
        setRows(dynamicRows);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  const columns = useMemo<DataTableColumn<GalleryCollectionRow>[]>(
    () => [
      { id: "member", header: "Member", accessor: (r) => r.member, sortValue: (r) => r.member, className: "font-medium" },
      {
        id: "group",
        header: "Group",
        accessor: (r) => r.group,
        sortValue: (r) => r.group,
        filterable: true,
        filterOptions: groupOptions,
        filterValue: (r) => r.group,
      },
      {
        id: "amount",
        header: "Amount",
        accessor: (r) => <CurrencyDisplay amount={r.amount} />,
        sortValue: (r) => r.amount,
        align: "right",
        footerValue: (rowsData) => <CurrencyDisplay amount={rowsData.reduce((s, r) => s + r.amount, 0)} />,
        className: "tnum",
      },
      { id: "date", header: "Date", accessor: (r) => formatDate(r.date), sortValue: (r) => new Date(r.date).getTime() },
      {
        id: "status",
        header: "Status",
        accessor: (r) => <Badge variant={r.status}>{r.status[0].toUpperCase() + r.status.slice(1)}</Badge>,
        sortValue: (r) => r.status,
        filterable: true,
        filterOptions: GALLERY_STATUS_OPTIONS,
        filterValue: (r) => r.status,
      },
    ],
    [groupOptions]
  );

  return (
    <Card title="Data table" subtitle="Sorting · pagination · search · per-column filter · selection · totals · export · density" padding={false}>
      <div className="p-4 pb-0">
        {selectedRows.length > 0 ? (
          <div className="mb-3 flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-800">
            <i className="ri-checkbox-line text-base" />
            <span className="tnum">{selectedRows.length}</span> selected — ready for a bulk action.
          </div>
        ) : null}
      </div>
      <DataTable
        rows={rows}
        rowKey={(r) => r.id}
        columns={columns}
        searchable
        selectable
        onSelectionChange={setSelectedRows}
        showTotals
        exportable
        exportFilename="collections"
        globalSearch={(row, q) => `${row.member} ${row.group}`.toLowerCase().includes(q.toLowerCase())}
      />
    </Card>
  );
}